import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { portaleAPI } from '../utils/api';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../utils/constants';

const STATUS_BADGE: Record<string, string> = {
  bozza: 'bg-gray-100 text-gray-800',
  inviata: 'bg-blue-100 text-blue-800',
  accettata: 'bg-green-100 text-green-800',
  rifiutata: 'bg-red-100 text-red-800',
  scaduta: 'bg-amber-100 text-amber-800',
};

const STATUS_LABEL: Record<string, string> = {
  bozza: 'Bozza',
  inviata: 'Inviata',
  accettata: 'Accettata',
  rifiutata: 'Rifiutata',
  scaduta: 'Scaduta',
};

const MyOrders: React.FC = () => {
  const [page, setPage] = useState(1);
  const [stato, setStato] = useState('');
  const limit = 20;

  // Get profile to check mostra_prezzi
  const { data: profileData } = useQuery({
    queryKey: ['customer-profile'],
    queryFn: () => portaleAPI.getProfile(),
  });
  const mostraPrezzi = profileData?.data?.mostra_prezzi ?? false;

  const { data, isLoading } = useQuery({
    queryKey: ['my-orders', page, stato],
    queryFn: () => portaleAPI.getMyOrders({ page, limit, stato: stato || undefined }),
  });

  const orders = data?.data?.data || [];
  const pagination = data?.data?.pagination;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">I Miei Ordini</h1>
        <p className="text-gray-600 mt-1">Visualizza lo stato dei tuoi ordini</p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex gap-4">
          <select
            value={stato}
            onChange={(e) => { setStato(e.target.value); setPage(1); }}
            className="block w-full sm:w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
          >
            <option value="">Tutti gli stati</option>
            <option value="bozza">Bozza</option>
            <option value="inviata">Inviata</option>
            <option value="accettata">Accettata</option>
            <option value="rifiutata">Rifiutata</option>
            <option value="scaduta">Scaduta</option>
          </select>
        </div>
      </div>

      {/* Orders List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Caricamento...</div>
        ) : orders.length === 0 ? (
          <div className="p-8 text-center">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun ordine trovato</h3>
            <p className="text-gray-500 mb-4">
              {stato ? 'Prova a modificare i filtri' : 'Non hai ancora effettuato ordini'}
            </p>
            <Link
              to="/catalog"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              Vai al Catalogo
            </Link>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Numero
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Titolo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stato
                </th>
                {mostraPrezzi && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Importo
                  </th>
                )}
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">

                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {order.numero}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {order.titolo}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(order.created_at).toLocaleDateString('it-IT')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[order.stato] || 'bg-gray-100 text-gray-800'}`}>
                      {STATUS_LABEL[order.stato] || order.stato}
                    </span>
                  </td>
                  {mostraPrezzi && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(order.importo_totale)}
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <Link
                      to={`/my-orders/${order.id}`}
                      className="text-primary-600 hover:text-primary-800 font-medium"
                    >
                      Dettaglio
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg shadow">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Precedente
            </button>
            <button
              onClick={() => setPage(Math.min(pagination.pages, page + 1))}
              disabled={page >= pagination.pages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Successivo
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <p className="text-sm text-gray-700">
              Mostrando da <span className="font-medium">{(page - 1) * limit + 1}</span> a{' '}
              <span className="font-medium">{Math.min(page * limit, pagination.total)}</span> di{' '}
              <span className="font-medium">{pagination.total}</span> ordini
            </p>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Precedente
              </button>
              {[...Array(Math.min(pagination.pages, 7))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      pageNum === page
                        ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                disabled={page >= pagination.pages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Successivo
              </button>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOrders;
